import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Icon,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import MaestroNavbar from "./blocks/MaestroNavbar.js";
import MaestroFooter from "./blocks/MaestroFooter.js";
import Seo from "./blocks/Seo.js";
import { getApiRoute, getPageRoute } from "src/services";

/**
 * Standalone Join the waitlist page (route: /waitlist).
 *
 * Reachable from any "Join the waitlist" button on the homepage and from
 * the top-nav CTA. Collects a richer set of fields than the homepage's
 * inline email capture so the follow-up email can be tailored.
 *
 * Submit handler is stubbed to console.log for now; wire to the real
 * intake endpoint in a follow-up commit.
 */
const WaitlistPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    role: "teacher",
    firstName: "",
    lastName: "",
    email: "",
    school: "",
    location: "",
    subject: "",
    grade: "",
    pilotScale: "",
    notes: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const setField = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setErrorMessage("");
    try {
      await axios.post(getApiRoute("waitlist", "SUBMIT"), form, {
        withCredentials: true,
      });
      setSubmitted(true);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "We couldn't save your submission. Please try again shortly.";
      setErrorMessage(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: "#fafafa",
      borderRadius: 1.5,
      fontSize: "0.9375rem",
      "& fieldset": { borderColor: theme.palette.text.tertiary },
      "&:hover fieldset": { borderColor: theme.palette.text.secondary },
      "&.Mui-focused fieldset": { borderColor: theme.palette.success.main },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.success.main,
    },
  };

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: theme.palette.paper.primary,
        minHeight: "100vh",
      }}
    >
      <Seo
        title="Join the waitlist · Maestro"
        description="Join the Maestro fall 2026 waitlist. AI + coaching for K-12 STEM teachers, delivered where you already work."
      />

      <MaestroNavbar />

      <Box
        component="section"
        sx={{
          py: { xs: 8, md: 12 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* soft green glow behind the form to match the homepage waitlist */}
        <Box
          sx={{
            position: "absolute",
            top: "-100px",
            right: "-100px",
            width: 500,
            height: 500,
            borderRadius: "50%",
            bgcolor: "#dcfce7",
            opacity: 0.5,
            filter: "blur(50px)",
            zIndex: 0,
          }}
        />

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <Button
            onClick={() => navigate(getPageRoute("home", "HOME"))}
            sx={{
              color: theme.palette.text.secondary,
              fontSize: "0.875rem",
              fontWeight: 600,
              textTransform: "none",
              mb: 3,
              px: 0,
              "&:hover": {
                bgcolor: "transparent",
                color: theme.palette.text.primary,
              },
            }}
          >
            ← Back to homepage
          </Button>

          {submitted ? (
            <Box
              sx={{
                textAlign: "center",
                py: { xs: 6, md: 10 },
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  bgcolor: "#dcfce7",
                  mb: 4,
                }}
              >
                <Icon
                  baseClassName="material-symbols-outlined"
                  sx={{
                    fontSize: 40,
                    color: theme.palette.success.main,
                  }}
                >
                  check_circle
                </Icon>
              </Box>
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: "2rem", md: "2.75rem" },
                  fontWeight: 800,
                  letterSpacing: "-0.03em",
                  lineHeight: 1.1,
                  mb: 2,
                }}
              >
                You're on the list.
              </Typography>
              <Typography
                sx={{
                  fontSize: "1.125rem",
                  color: theme.palette.text.secondary,
                  maxWidth: 520,
                  mx: "auto",
                  lineHeight: 1.5,
                }}
              >
                We'll be in touch within a week. If you're a fit for the fall
                2026 cohort, we'll set up a short call to walk you through
                what onboarding looks like.
              </Typography>
              <Button
                onClick={() => navigate(getPageRoute("home", "HOME"))}
                sx={{
                  mt: 5,
                  bgcolor: theme.palette.text.primary,
                  color: theme.palette.paper.primary,
                  px: 4,
                  py: 1.75,
                  fontSize: "0.9375rem",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: 999,
                  "&:hover": { bgcolor: "#1a1a1a" },
                }}
              >
                Back to homepage
              </Button>
            </Box>
          ) : (
            <>
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: "2.5rem", md: "4rem" },
                  fontWeight: 800,
                  letterSpacing: "-0.03em",
                  lineHeight: 1.02,
                  color: theme.palette.text.primary,
                }}
              >
                Bring Maestro to{" "}
                <Box
                  component="span"
                  sx={{ color: theme.palette.success.main }}
                >
                  your classroom
                </Box>
                .
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "1.0625rem", md: "1.25rem" },
                  color: theme.palette.text.secondary,
                  mt: 3,
                  maxWidth: 620,
                  lineHeight: 1.55,
                }}
              >
                A few questions so we can tailor the follow-up. We read
                everything and get back within a week.
              </Typography>

              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  mt: 6,
                  p: { xs: 3.5, md: 5 },
                  bgcolor: theme.palette.paper.primary,
                  border: `1px solid ${theme.palette.text.tertiary}`,
                  borderRadius: 3,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
                }}
              >
                {/* Role picker */}
                <Box sx={{ mb: 4 }}>
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      mb: 1.5,
                    }}
                  >
                    I'm signing up as a…
                  </Typography>
                  <RadioGroup
                    row
                    value={form.role}
                    onChange={setField("role")}
                    sx={{ gap: 1, flexWrap: "wrap" }}
                  >
                    {[
                      { value: "teacher", label: "Teacher" },
                      { value: "school-leader", label: "School leader" },
                      { value: "district-partner", label: "District partner" },
                    ].map((opt) => (
                      <FormControlLabel
                        key={opt.value}
                        value={opt.value}
                        control={
                          <Radio
                            sx={{
                              color: theme.palette.text.tertiary,
                              "&.Mui-checked": {
                                color: theme.palette.success.main,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              fontSize: "0.9375rem",
                              fontWeight: 500,
                              color: theme.palette.text.primary,
                            }}
                          >
                            {opt.label}
                          </Typography>
                        }
                      />
                    ))}
                  </RadioGroup>
                </Box>

                {/* Name row */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <TextField
                    label="First name"
                    required
                    value={form.firstName}
                    onChange={setField("firstName")}
                    fullWidth
                    sx={inputSx}
                  />
                  <TextField
                    label="Last name"
                    required
                    value={form.lastName}
                    onChange={setField("lastName")}
                    fullWidth
                    sx={inputSx}
                  />
                </Box>

                {/* Email */}
                <TextField
                  label="School email"
                  required
                  type="email"
                  value={form.email}
                  onChange={setField("email")}
                  fullWidth
                  sx={{ ...inputSx, mb: 2 }}
                />

                {/* School + Location row */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <TextField
                    label={
                      form.role === "district-partner"
                        ? "District name"
                        : "School / district name"
                    }
                    required
                    value={form.school}
                    onChange={setField("school")}
                    fullWidth
                    sx={inputSx}
                  />
                  <TextField
                    label="City, State"
                    required
                    value={form.location}
                    onChange={setField("location")}
                    fullWidth
                    sx={inputSx}
                  />
                </Box>

                {/* Teacher-only fields */}
                {form.role === "teacher" && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <TextField
                      select
                      label="What do you teach?"
                      value={form.subject}
                      onChange={setField("subject")}
                      fullWidth
                      sx={inputSx}
                    >
                      {[
                        "Math",
                        "Science",
                        "Both math and science",
                        "Other STEM",
                      ].map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Grade band"
                      value={form.grade}
                      onChange={setField("grade")}
                      fullWidth
                      sx={inputSx}
                    >
                      {["K-2", "3-5", "6-8", "9-12"].map((g) => (
                        <MenuItem key={g} value={g}>
                          {g}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                )}

                {/* District-only fields */}
                {form.role === "district-partner" && (
                  <TextField
                    select
                    label="Pilot scale you're considering"
                    value={form.pilotScale}
                    onChange={setField("pilotScale")}
                    fullWidth
                    sx={{ ...inputSx, mb: 2 }}
                  >
                    {[
                      "1 school (5-10 teachers)",
                      "2-3 schools (15-30 teachers)",
                      "District-wide (50+ teachers)",
                      "Exploring, not sure yet",
                    ].map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                {/* Free-text notes */}
                <TextField
                  label="Anything you want us to know? (optional)"
                  multiline
                  rows={3}
                  value={form.notes}
                  onChange={setField("notes")}
                  fullWidth
                  sx={inputSx}
                />

                {/* Error surface */}
                {errorMessage && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      bgcolor: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderRadius: 1.5,
                      color: "#991b1b",
                      fontSize: "0.9375rem",
                    }}
                  >
                    {errorMessage}
                  </Box>
                )}

                {/* Submit */}
                <Box sx={{ mt: 4, textAlign: "center" }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting}
                    sx={{
                      bgcolor: theme.palette.success.main,
                      color: theme.palette.paper.primary,
                      px: { xs: 4, md: 5.5 },
                      py: { xs: 2.25, md: 2.5 },
                      fontSize: { xs: "1rem", md: "1.125rem" },
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: 999,
                      whiteSpace: "nowrap",
                      boxShadow: "0 8px 24px rgba(32,156,51,0.35)",
                      "&.Mui-disabled": {
                        bgcolor: theme.palette.text.tertiary,
                        color: theme.palette.paper.primary,
                        boxShadow: "none",
                      },
                      "&:hover": {
                        bgcolor: "#15803d",
                        transform: "translateY(-2px)",
                        boxShadow: "0 10px 28px rgba(32,156,51,0.45)",
                      },
                      transition:
                        "transform 0.15s, background-color 0.15s, box-shadow 0.15s",
                    }}
                  >
                    {submitting ? "Submitting…" : "Join the waitlist →"}
                  </Button>
                  <Typography
                    sx={{
                      fontSize: "0.8125rem",
                      color: theme.palette.text.secondary,
                      mt: 2.5,
                      opacity: 0.75,
                    }}
                  >
                    We read every submission and get back within a week.
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Container>
      </Box>

      <MaestroFooter />
    </Box>
  );
};

export default WaitlistPage;
